import { Button } from '@/components/Button'
import { extractColors } from 'extract-colors'
import { Input } from '@/components/form/Input'
import { useForm } from 'react-hook-form'

import { MusicFormDataProps, MusicResponseProps } from '@/types/musicProps'
import {
  DiscoveryStatus,
  PlaybackStatus,
  VisualAssetStatus,
  playbackStatusOptions,
  visualAssetStatusOptions,
} from '@/types/catalogGovernance'

import { DragEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Select } from '../form/Select'
import { ArtistsResponseProps } from '@/types/artistsProps'
import { useToast } from '@/hooks/useToast'
import { IoClose, IoImage, IoMusicalNote, IoTrash } from 'react-icons/io5'

import { useModal } from '@/hooks/useModal'
import { useDispatch, useSelector } from 'react-redux'
import { ReduxProps } from '@/storage'
import {
  ArtistsProps,
  handleSetArtists,
} from '@/storage/modules/artists/reducer'
import { LicensesProps } from '@/storage/modules/licenses/reducer'
import { MoodsProps } from '@/storage/modules/moods/reducer'
import { AlbumsProps, AlbumsDataProps } from '@/types/albumsProps'
import {
  TrackListRemoteProps,
  handleTrackListRemote,
} from '@/storage/modules/trackListRemote/reducer'
import { FormArtist } from '../FormArtist'
import colors from 'tailwindcss/colors'
import { FileWithType, useUpload } from '@/hooks/useUpload'

import { api } from '@/services/api'
import { MoodResponseProps } from '@/types/moodsProps'
import {
  type PaginatedResponseProps,
  normalizePaginationMeta,
} from '@/types/paginationProps'
import { uploadMusicAssets } from '@/services/upload-music-assets'
import { AxiosError } from 'axios'
import { CatalogStatusBadge } from '../CatalogStatusBadge'
import { MusicalGenresDataProps } from '@/types/musicalGenresProps'
import { fetchGenresOptions } from '@/services/genres'

interface FormMusicProps {
  musicId?: string
}

type SelectedArtistProps = {
  id: string
  title: string
  photoURL: string
  publicPhotoURL: string | null
  musicalGenres: string
  isMain: boolean
}

const infoCardClassName = 'rounded-2xl border border-gray-200 bg-gray-50/60 p-4'

const dropzoneClassName =
  'relative flex min-h-[104px] items-center justify-center overflow-hidden rounded-xl border border-dashed border-purple-600 px-4 py-4'

export function FormMusic({ musicId }: FormMusicProps) {
  const [selectedArtists, setSelectedArtists] = useState<SelectedArtistProps[]>(
    [],
  )
  const [selectedMoods, setSelectedMoods] = useState<MoodResponseProps[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [isPublisherLoading, setIsPublisherLoading] = useState(false)
  const [governanceActionLoading, setGovernanceActionLoading] = useState<
    'visualAsset' | 'playback' | null
  >(null)

  const [isDropArtwork, setIsDropArtwork] = useState(false)
  const [isDropMusic, setIsDropMusic] = useState(false)
  const [fileArtwork, setFileArtwork] = useState<FileWithType>()
  const [fileMusic, setFileMusic] = useState<FileWithType>()
  const [music, setMusic] = useState<MusicResponseProps>()

  const [paletColorsArtwork, setPaletColorsArtwork] = useState<string[]>([])
  const [selectColor, setSelectColor] = useState('')
  const [artistSearchQuery, setArtistSearchQuery] = useState('')
  const [artistSearchResults, setArtistSearchResults] = useState<
    ArtistsResponseProps[]
  >([])
  const [isLoadingArtistSearch, setIsLoadingArtistSearch] = useState(false)
  const [isArtistDropdownOpen, setIsArtistDropdownOpen] = useState(false)
  const [genreSearchQuery, setGenreSearchQuery] = useState('')
  const [genreOptions, setGenreOptions] = useState<MusicalGenresDataProps[]>([])
  const [isLoadingGenreOptions, setIsLoadingGenreOptions] = useState(false)
  const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false)
  const [selectedGenre, setSelectedGenre] =
    useState<MusicalGenresDataProps | null>(null)
  const [albumSearchQuery, setAlbumSearchQuery] = useState('')
  const [isAlbumDropdownOpen, setIsAlbumDropdownOpen] = useState(false)
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumsDataProps | null>(
    null,
  )
  const [hasAlbum, setHasAlbum] = useState(false)
  const [discoveryStatus, setDiscoveryStatus] =
    useState<DiscoveryStatus>('DRAFT')
  const [visualAssetStatus, setVisualAssetStatus] =
    useState<VisualAssetStatus>('NONE')
  const [playbackStatus, setPlaybackStatus] =
    useState<PlaybackStatus>('DISABLED')
  const artistSearchRequestIdRef = useRef(0)
  const genreSearchRequestIdRef = useRef(0)

  const { register, setValue, handleSubmit } = useForm<MusicFormDataProps>()

  const { showToast } = useToast()
  const dispatch = useDispatch()
  const { closeModal, openModal } = useModal()

  const { formatBytes, formatDate, copyObjectUrl } = useUpload()

  const { artists } = useSelector<ReduxProps, ArtistsProps>(
    (state) => state.artists,
  )

  const { licenses } = useSelector<ReduxProps, LicensesProps>(
    (state) => state.licenses,
  )
  const { moods } = useSelector<ReduxProps, MoodsProps>((state) => state.moods)
  const { albums } = useSelector<ReduxProps, AlbumsProps>(
    (state) => state.albums,
  )
  const { currentPage } = useSelector<ReduxProps, TrackListRemoteProps>(
    (state) => state.trackListRemote,
  )

  const reloadLoadedMusics = async () => {
    const pagesToLoad = Math.max(currentPage, 1)

    const responses = await Promise.all(
      Array.from({ length: pagesToLoad }, (_, index) =>
        api.get<PaginatedResponseProps<MusicResponseProps>>('/musics', {
          params: {
            page: index + 1,
          },
        }),
      ),
    )

    const mergedMusics = responses.flatMap((response) => response.data.data)
    const lastResponse = responses[responses.length - 1]
    const paginationMeta = normalizePaginationMeta(
      lastResponse.data.meta,
      mergedMusics.length,
    )

    dispatch(
      handleTrackListRemote({
        trackListRemote: mergedMusics,
        currentPage: paginationMeta.currentPage,
        lastPage: paginationMeta.lastPage,
        totalItems: paginationMeta.totalItems,
      }),
    )
  }

  const reloadArtists = async () => {
    const responseArtists = await api
      .get('/artists')
      .then((res) => res.data.data as ArtistsResponseProps[])

    dispatch(handleSetArtists({ artists: responseArtists }))
  }

  const handleUpdateMusic = async (data: MusicFormDataProps, id: string) => {
    if (id) {
      await api
        .patch(`/musics/${id}`, data)
        .then(async () => {
          let musicUrl = data.url
          let artworkUrl = data.artwork
          let publicArtworkURL =
            data.publicArtworkURL ?? music?.publicArtworkURL

          if (fileArtwork || fileMusic) {
            const mainArtist =
              selectedArtists.find((a) => a.isMain) ?? selectedArtists[0]
            const uploadedFiles = await uploadMusicAssets({
              artworkFile: fileArtwork,
              audioFile: fileMusic,
              slugSource: mainArtist.title,
            })

            if (uploadedFiles.audioFile?.url) {
              musicUrl = uploadedFiles.audioFile.url
            }

            if (uploadedFiles.privateArtworkFile?.url) {
              artworkUrl = uploadedFiles.privateArtworkFile.url
            }

            if (uploadedFiles.publicArtworkFile?.url) {
              publicArtworkURL = uploadedFiles.publicArtworkFile.url
            }
          }

          await api.patch(`/musics/${id}`, {
            url: musicUrl,
            artwork: artworkUrl,
            publicArtworkURL,
          })

          await reloadArtists()

          showToast('Music updated successfully', {
            type: 'success',
            theme: 'light',
          })

          await reloadLoadedMusics()

          setIsLoading(false)
          closeModal()
        })
        .catch((err) => {
          const errResponse = err.response.data
          setIsLoading(false)
          showToast(`${errResponse.message}`, {
            type: 'error',
            theme: 'light',
          })
        })
    }
  }

  const handleSaveMusic = async (data: MusicFormDataProps) => {
    await api
      .post('/musics', data)
      .then(async (response) => {
        const responseMusic = response.data as MusicResponseProps

        let musicUrl = data.url
        let artworkUrl = data.artwork
        let publicArtworkURL = data.publicArtworkURL

        if (fileArtwork || fileMusic) {
          const mainArtist =
            selectedArtists.find((a) => a.isMain) ?? selectedArtists[0]
          const uploadedFiles = await uploadMusicAssets({
            artworkFile: fileArtwork,
            audioFile: fileMusic,
            slugSource: mainArtist.title,
          })

          if (uploadedFiles.audioFile?.url) {
            musicUrl = uploadedFiles.audioFile.url
          }

          if (uploadedFiles.privateArtworkFile?.url) {
            artworkUrl = uploadedFiles.privateArtworkFile.url
          }

          if (uploadedFiles.publicArtworkFile?.url) {
            publicArtworkURL = uploadedFiles.publicArtworkFile.url
          }
        }

        await api.patch(`/musics/${responseMusic.id}`, {
          url: musicUrl,
          artwork: artworkUrl,
          publicArtworkURL,
        })

        showToast('Music added successfully', {
          type: 'success',
          theme: 'light',
        })

        await reloadArtists()

        await reloadLoadedMusics()

        setIsLoading(false)
        closeModal()
      })
      .catch((err) => {
        const errResponse = err.response.data
        setIsLoading(false)
        showToast(`${errResponse.message}`, {
          type: 'error',
          theme: 'light',
        })
      })
  }

  const handlePublisherMusic = async (id: string) => {
    setIsPublisherLoading(true)

    try {
      let operationLabel = ''

      if (discoveryStatus === 'PUBLISHED') {
        await api.patch(`/musics/${id}/unpublish`)
        operationLabel = 'unpublished'
        setDiscoveryStatus('HIDDEN')
      } else {
        await api.patch(`/musics/${id}/publish`)
        operationLabel = 'published'
        setDiscoveryStatus('PUBLISHED')
      }

      await reloadArtists()
      await reloadLoadedMusics()

      showToast(`Music ${operationLabel} successfully`, {
        type: 'success',
        theme: 'light',
      })

      closeModal()
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message || 'Error updating music publication'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setIsPublisherLoading(false)
    }
  }

  const handleUpdateMusicVisualAssetStatus = async () => {
    if (!music?.id) return

    setGovernanceActionLoading('visualAsset')

    try {
      await api.patch(`/musics/${music.id}/visual-asset-status`, {
        visualAssetStatus,
      })

      await reloadLoadedMusics()

      showToast('Music visual asset status updated successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message ||
        'Error updating music visual asset status'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setGovernanceActionLoading(null)
    }
  }

  const handleUpdateMusicPlaybackStatus = async () => {
    if (!music?.id) return

    setGovernanceActionLoading('playback')

    try {
      await api.patch(`/musics/${music.id}/playback-status`, {
        playbackStatus,
      })

      await reloadLoadedMusics()

      showToast('Music playback status updated successfully', {
        type: 'success',
        theme: 'light',
      })
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>
      const message =
        error.response?.data?.message || 'Error updating music playback status'

      showToast(message, {
        type: 'error',
        theme: 'light',
      })
    } finally {
      setGovernanceActionLoading(null)
    }
  }

  async function submit(data: MusicFormDataProps) {
    if (selectedArtists.length < 1) {
      showToast('Select at least one artist', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (!fileArtwork && !music?.artwork) {
      showToast('Drop a music artwork', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (!fileMusic && !music?.url) {
      showToast('Drop a music', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    // Validação: se tem album, deve ter albumId e trackNumber
    if (hasAlbum) {
      if (!data.albumId) {
        showToast('Please select an album', {
          type: 'error',
          theme: 'light',
        })
        return
      }

      if (!data.trackNumber) {
        showToast('Please enter a track number', {
          type: 'error',
          theme: 'light',
        })
        return
      }
    } else {
      // Se não tem album, limpar os valores
      data.albumId = null
      data.trackNumber = null
    }

    if (!data.genreId) {
      showToast('Select at least one musical genre', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (!data.licenseId) {
      showToast('Select a license', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (selectedMoods.length === 0) {
      showToast('Select at least one mood', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    if (!data.color) {
      showToast('Select a color', {
        type: 'error',
        theme: 'light',
      })

      return
    }

    setIsLoading(true)

    const mainArtist =
      selectedArtists.find((a) => a.isMain) ?? selectedArtists[0]

    const newMusic = {
      artistIds: selectedArtists.map((artist) => artist.id),
      mainArtistId: mainArtist?.id ?? null,
      url: data.url ? data.url : 'https://i.ibb.co/hZ7QNB3/sonoriza.png',
      artwork: data.artwork
        ? data.artwork
        : 'https://i.ibb.co/hZ7QNB3/sonoriza.png',
      publicArtworkURL: music?.publicArtworkURL ?? undefined,
      genreId: data.genreId,
      licenseId: data.licenseId,
      moodIds: selectedMoods.map((mood) => mood.id),
      color: data.color,
      title: data.title,
      albumId: data.albumId ?? null,
      trackNumber: data.trackNumber ? Number(data.trackNumber) : null,
    }

    if (data.id) {
      handleUpdateMusic(newMusic, data.id)
    } else {
      handleSaveMusic(newMusic)
    }
  }

  const normalizedArtistSearchQuery = artistSearchQuery.trim()
  const normalizedGenreSearchQuery = genreSearchQuery.trim()
  const normalizedAlbumSearchQuery = albumSearchQuery.trim()

  const artistSuggestions = useMemo(() => {
    const selectedArtistsMap = new Set(
      selectedArtists.map((artist) => artist.id),
    )
    return artistSearchResults.filter(
      (artist) => !selectedArtistsMap.has(artist.id),
    )
  }, [artistSearchResults, selectedArtists])

  const genreSuggestions = useMemo(() => {
    if (normalizedGenreSearchQuery.length < 1) {
      return []
    }

    const normalizedQuery = normalizedGenreSearchQuery.toLowerCase()

    return genreOptions.filter((genre) =>
      genre.title.toLowerCase().includes(normalizedQuery),
    )
  }, [genreOptions, normalizedGenreSearchQuery])

  const albumSuggestions = useMemo(() => {
    if (normalizedAlbumSearchQuery.length < 1) {
      return []
    }

    const normalizedQuery = normalizedAlbumSearchQuery.toLowerCase()

    return albums.filter((album) =>
      album.title.toLowerCase().includes(normalizedQuery),
    )
  }, [albums, normalizedAlbumSearchQuery])

  const licensesOptions = useMemo(() => {
    const options = licenses?.map((license) => {
      return {
        label: license.title,
        value: license.id,
      }
    })

    if (!options) return

    return [{ label: 'Select a license', value: '' }, ...options]
  }, [licenses])

  const moodsOptions = useMemo(() => {
    const options = moods?.map((mood) => {
      return {
        label: mood.title,
        value: mood.id,
      }
    })

    if (!options) return

    return [{ label: 'Select one or more', value: '' }, ...options]
  }, [moods])

  const handleSelectedArtists = (selectedArtist: ArtistsResponseProps) => {
    const { id, title, photoURL, musicalGenres, publicPhotoURL } =
      selectedArtist

    const isArtistsExists = selectedArtists.find((item) => item.id === id)
    if (isArtistsExists) return

    const isFirst = selectedArtists.length === 0

    setSelectedArtists([
      ...selectedArtists,
      {
        id,
        title,
        photoURL: photoURL ?? publicPhotoURL ?? '',
        publicPhotoURL,
        musicalGenres: musicalGenres.map((item) => item.title).join(', '),
        isMain: isFirst,
      },
    ])
    setArtistSearchQuery('')
    setArtistSearchResults([])
    setIsArtistDropdownOpen(false)
    setValue('artistIds', [])
  }

  const handleSelectGenre = (genre: MusicalGenresDataProps) => {
    setSelectedGenre(genre)
    setGenreSearchQuery(genre.title)
    setValue('genreId', genre.id, { shouldValidate: true })
    setIsGenreDropdownOpen(false)
  }

  const handleSelectAlbum = (album: AlbumsDataProps) => {
    setSelectedAlbum(album)
    setAlbumSearchQuery(album.title)
    setValue('albumId', album.id, { shouldValidate: true })
    setIsAlbumDropdownOpen(false)
  }

  const handleRemoveArtists = (artistRemoveId: string) => {
    const wasMain = selectedArtists.find((item) => item.id === artistRemoveId)
      ?.isMain
    const remaining = selectedArtists.filter(
      (item) => item.id !== artistRemoveId,
    )

    if (wasMain && remaining.length > 0) {
      setSelectedArtists([
        { ...remaining[0], isMain: true },
        ...remaining.slice(1),
      ])
    } else {
      setSelectedArtists(remaining)
    }
  }

  const handleSetMainArtist = (mainId: string) => {
    setSelectedArtists(
      selectedArtists.map((artist) => ({
        ...artist,
        isMain: artist.id === mainId,
      })),
    )
  }

  const handleSelectedMood = (moodId: string) => {
    const moodSelected = moods.find((item) => item.id === moodId)

    if (!moodSelected) return

    const isMoodExists = selectedMoods.find((item) => item.id === moodId)
    if (isMoodExists) return

    setSelectedMoods((prev) => [...prev, moodSelected])
    setValue('moodIds', [])
  }

  const handleRemoveMood = (moodRemoveId: string) => {
    const filter = selectedMoods.filter((item) => item.id !== moodRemoveId)
    setSelectedMoods(filter)
  }

  function dropObject(e: DragEvent<HTMLDivElement>, type: 'music' | 'artwork') {
    e.preventDefault()

    const file = e.dataTransfer.files[0]

    const imageTypes = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']

    if (type === 'artwork' && imageTypes.includes(file.type)) {
      setFileArtwork(file)
      setIsDropArtwork(false)

      return
    }

    if (type === 'music' && file.type === 'audio/mpeg') {
      setFileMusic(file)
      setIsDropMusic(false)

      return
    }

    showToast('Archive type no suported', {
      type: 'error',
      theme: 'light',
    })
    setIsDropMusic(false)
    setIsDropArtwork(false)
  }

  const handleExtractColor = async (file: File) => {
    try {
      await extractColors(URL.createObjectURL(file)).then((result) => {
        const colors = result.map((palet) => palet.hex)
        setPaletColorsArtwork(colors)
      })
    } catch (error) {
      showToast('Erro fetch colors', {
        type: 'error',
        theme: 'light',
      })
    }
  }

  useEffect(() => {
    let isMounted = true
    const requestId = artistSearchRequestIdRef.current + 1
    artistSearchRequestIdRef.current = requestId

    const timer = window.setTimeout(async () => {
      if (normalizedArtistSearchQuery.length < 2) {
        setArtistSearchResults([])
        setIsLoadingArtistSearch(false)
        return
      }

      try {
        setIsLoadingArtistSearch(true)

        const response = await api.get('/artists', {
          params: {
            page: 1,
            limit: 50,
            title: normalizedArtistSearchQuery,
          },
        })

        if (!isMounted || artistSearchRequestIdRef.current !== requestId) return

        const payload = response.data as
          | ArtistsResponseProps[]
          | {
              data?: ArtistsResponseProps[] | { data?: ArtistsResponseProps[] }
            }

        let artistsFromApi: ArtistsResponseProps[] = []

        if (Array.isArray(payload)) {
          artistsFromApi = payload
        } else if (Array.isArray(payload?.data)) {
          artistsFromApi = payload.data
        } else if (
          typeof payload?.data === 'object' &&
          payload.data !== null &&
          'data' in payload.data &&
          Array.isArray(payload.data.data)
        ) {
          artistsFromApi = payload.data.data
        }

        setArtistSearchResults(artistsFromApi)
      } catch (error) {
        if (!isMounted || artistSearchRequestIdRef.current !== requestId) return

        showToast('Error fetching artists', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted && artistSearchRequestIdRef.current === requestId) {
          setIsLoadingArtistSearch(false)
        }
      }
    }, 250)

    return () => {
      isMounted = false
      window.clearTimeout(timer)
    }
  }, [normalizedArtistSearchQuery, showToast])

  useEffect(() => {
    let isMounted = true
    const requestId = genreSearchRequestIdRef.current + 1
    genreSearchRequestIdRef.current = requestId

    const loadGenres = async () => {
      try {
        setIsLoadingGenreOptions(true)
        const genresResponse = await fetchGenresOptions()

        if (!isMounted || genreSearchRequestIdRef.current !== requestId) return

        setGenreOptions(genresResponse)
      } catch (error) {
        if (!isMounted || genreSearchRequestIdRef.current !== requestId) return

        showToast('Error fetching genres', {
          type: 'error',
          theme: 'light',
        })
      } finally {
        if (isMounted && genreSearchRequestIdRef.current === requestId) {
          setIsLoadingGenreOptions(false)
        }
      }
    }

    loadGenres()

    return () => {
      isMounted = false
    }
  }, [showToast])

  useEffect(() => {
    if (musicId) {
      const fetchMusic = async () => {
        try {
          const response = await api
            .get(`/musics/${musicId}`)
            .then((res) => res.data as MusicResponseProps)
          setMusic(response)
        } catch (error) {
          showToast('Error fetching music details', {
            type: 'error',
            theme: 'light',
          })
        }
      }

      fetchMusic()
    }
  }, [musicId, showToast])

  useEffect(() => {
    if (music) {
      setValue('artwork', music.artwork)
      setValue('publicArtworkURL', music.publicArtworkURL ?? undefined)
      setValue('color', music.color)
      setValue('title', music.title)
      setValue('url', music.url)
      setValue('genreId', music.genreId)
      setValue('licenseId', music.licenseId)
      setValue('id', music.id)
      // Mostrar apenas o primeiro index no select quando há múltiplos
      setValue(
        'moodIds',
        music.moodIds.length > 1 ? [music.moodIds[0]] : music.moodIds,
      )
      setValue('albumId', music.albumId ?? '')
      setValue(
        'trackNumber',
        music.trackNumber ? Number(music.trackNumber) : undefined,
      )

      setValue('artistIds', [])

      setSelectedArtists(
        music.artists.map((artist) => ({
          id: artist.id,
          title: artist.title,
          photoURL: artist.photoURL ?? artist.publicPhotoURL ?? '',
          publicPhotoURL: artist.publicPhotoURL,
          musicalGenres: artist.musicalGenres
            .map((item) => item.title)
            .join(', '),
          isMain: artist.isMain ?? artist.id === music.mainArtistId,
        })),
      )

      setSelectedMoods(music.moods)
      setDiscoveryStatus(music.discoveryStatus)
      setVisualAssetStatus(music.visualAssetStatus)
      setPlaybackStatus(music.playbackStatus)
      setHasAlbum(!!music.albumId)
    }
  }, [music, setValue])

  useEffect(() => {
    if (!music?.genreId || genreOptions.length === 0) {
      return
    }

    const genreFromMusic = genreOptions.find(
      (genre) => genre.id === music.genreId,
    )

    if (!genreFromMusic) {
      return
    }

    setSelectedGenre(genreFromMusic)
    setGenreSearchQuery(genreFromMusic.title)
  }, [genreOptions, music?.genreId])

  useEffect(() => {
    if (!music?.albumId || albums.length === 0) {
      return
    }

    const albumFromMusic = albums.find((album) => album.id === music.albumId)

    if (!albumFromMusic) {
      return
    }

    setSelectedAlbum(albumFromMusic)
    setAlbumSearchQuery(albumFromMusic.title)
  }, [albums, music?.albumId])

  useEffect(() => {
    if (fileArtwork) {
      handleExtractColor(fileArtwork)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileArtwork])

  return (
    <>
      <h1 className="font-bold text-xl text-purple-600">
        {music ? 'Edit' : 'Add new'} music
      </h1>
      <div className="h-[1px] bg-gray-300/50 my-7" />

      <div className="grid grid-cols-[1fr,220px] gap-4 base:grid-cols-1 md:grid-cols-1">
        {selectedArtists.length > 0 && (
          <div className={infoCardClassName}>
            <p className="font-semibold mb-2">Artists</p>
            <div className="grid grid-cols-4 gap-3 base:grid-cols-3 md:grid-cols-2">
              {selectedArtists.map((artist) => (
                <div
                  key={artist.id}
                  className={`relative overflow-hidden rounded-xl border bg-white p-3 ${
                    artist.isMain ? 'border-purple-500' : 'border-gray-200'
                  }`}
                >
                  {artist.isMain && (
                    <span className="absolute left-2 top-2 rounded-full bg-purple-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                      Main
                    </span>
                  )}
                  <button
                    className="flex w-full flex-col items-center justify-center overflow-hidden"
                    onClick={() => {
                      openModal({
                        children: (
                          <FormArtist
                            artist={artists.find(
                              (item) => item.id === artist.id,
                            )}
                          />
                        ),
                      })
                    }}
                  >
                    <img
                      src={artist.photoURL}
                      alt="photo artists"
                      title={artist.title}
                      className="mt-4 h-20 w-20 rounded-xl object-cover"
                    />
                    <p className="mt-2 break-words text-center text-sm font-medium leading-tight">
                      {artist.title}
                    </p>
                  </button>
                  <div className="mt-2 flex gap-1">
                    {!artist.isMain && (
                      <button
                        type="button"
                        className="flex-1 rounded-md bg-purple-50 px-1 py-1 text-[10px] font-semibold text-purple-700 hover:bg-purple-100"
                        title="Set as main artist"
                        onClick={() => handleSetMainArtist(artist.id)}
                      >
                        Set main
                      </button>
                    )}
                    {!music && (
                      <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100 hover:bg-red-200"
                        title="Remove"
                        onClick={() => handleRemoveArtists(artist.id)}
                      >
                        <IoClose color={'#dc2626'} size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {music?.artwork && (
          <div className={`${infoCardClassName} max-w-[220px] base:max-w-none`}>
            <p className="font-semibold mb-2">Artwork</p>
            <div>
              <img
                src={music.artwork}
                alt="artwork"
                className="h-28 w-28 rounded-xl object-cover"
              />
            </div>
          </div>
        )}
      </div>

      {selectedMoods.length > 0 && (
        <div className={`mb-4 mt-4 ${infoCardClassName}`}>
          <p className="font-semibold mb-2">Moods</p>
          <div className="grid grid-cols-4 gap-2 base:grid-cols-3 md:grid-cols-1">
            {selectedMoods.map((mood) => (
              <div
                key={mood.id}
                className="relative flex min-h-14 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-white p-2"
              >
                <button
                  type="button"
                  className="absolute right-0 top-0 bg-red-600 p-1 rounded-bl-md hover:bg-red-700"
                  onClick={() => handleRemoveMood(mood.id)}
                  title="Remove"
                >
                  <IoTrash color={colors.white} size={12} />
                </button>
                <p className="font-semibold text-center">{mood.title}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex max-w-3xl items-stretch justify-between gap-4 base:max-w-none base:flex-col">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsDropArtwork(true)}
          className={`${dropzoneClassName} flex-1`}
        >
          {fileArtwork?.name ? (
            <div className="flex w-full min-w-0 items-center gap-3">
              <img
                src={URL.createObjectURL(fileArtwork)}
                alt={fileArtwork.name}
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {fileArtwork.name}
                </p>
                <span className="text-[#71839B] text-xs font-normal">
                  {formatBytes(fileArtwork.size)} -{' '}
                  {formatDate(fileArtwork.lastModified)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  setFileArtwork(undefined)
                }}
                className="ml-auto shrink-0"
              >
                <IoTrash color={colors.red[600]} size={20} />
              </button>
            </div>
          ) : (
            <>
              {music?.artwork ? (
                <button
                  title="Copy a link"
                  className="grid grid-cols-[20px,auto] items-center justify-center gap-2 overflow-hidden px-4"
                  onClick={() => copyObjectUrl(music.artwork)}
                >
                  <IoImage color={colors.purple[600]} size={20} />
                  <p className="font-semibold text-purple-600 line-clamp-1">
                    {music.artwork}
                  </p>
                </button>
              ) : (
                <div className="flex gap-2 items-center justify-center">
                  <IoImage color={colors.purple[600]} size={20} />
                  <p className="font-semibold text-purple-600">
                    Drop a Artwork
                  </p>
                </div>
              )}
            </>
          )}
          {isDropArtwork && (
            <div
              onDrop={(e) => dropObject(e, 'artwork')}
              onDragLeave={() => setIsDropArtwork(false)}
              className="bg-black/80 w-full h-full absolute top-0 flex items-center justify-center "
            >
              <div className="pointer-events-none">
                <p className="text-white font-bold text-xl">Drop your file</p>
              </div>
            </div>
          )}
        </div>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsDropMusic(true)}
          className={`${dropzoneClassName} flex-1`}
        >
          {isDropMusic && (
            <div
              onDrop={(e) => dropObject(e, 'music')}
              onDragLeave={() => setIsDropMusic(false)}
              className="bg-black/80 w-full h-full absolute top-0 flex items-center justify-center "
            >
              <div className="pointer-events-none">
                <p className="text-white font-bold text-xl">Drop your file</p>
              </div>
            </div>
          )}

          {fileMusic ? (
            <div className="flex w-full min-w-0 items-center gap-3">
              <IoMusicalNote size={40} className="shrink-0" />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{fileMusic.name}</p>
                <span className="text-[#71839B] text-xs font-normal">
                  {formatBytes(fileMusic.size)} -{' '}
                  {formatDate(fileMusic.lastModified)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  setFileMusic(undefined)
                }}
                className="ml-auto shrink-0"
              >
                <IoTrash color={colors.red[600]} size={20} />
              </button>
            </div>
          ) : (
            <>
              {music ? (
                <button
                  title="Copy a link"
                  className="grid grid-cols-[20px,auto] items-center justify-center gap-1 overflow-hidden px-4"
                  onClick={() => copyObjectUrl(music.url)}
                >
                  <IoMusicalNote color={colors.purple[600]} size={20} />
                  <p className="font-semibold text-purple-600 line-clamp-1">
                    {music.url}
                  </p>
                </button>
              ) : (
                <div className="flex gap-2 items-center justify-center">
                  <IoMusicalNote color={colors.purple[600]} size={20} />
                  <p className="font-semibold text-purple-600">Drop a Music</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit(submit)} className="flex flex-col">
        <div>
          <div className="grid grid-cols-3 gap-4 base:grid-cols-2 md:grid-cols-1">
            <Input
              placeholder="Music Title"
              label="Title"
              name="title"
              register={register}
              required
            />
            <div className="relative">
              <h4 className="font-bold text-sm my-3">Artists</h4>
              <input type="hidden" {...register('artistIds')} />
              <input
                value={artistSearchQuery}
                onChange={(event) => {
                  const nextValue = event.currentTarget.value
                  setArtistSearchQuery(nextValue)
                  setIsArtistDropdownOpen(true)
                }}
                onFocus={() => setIsArtistDropdownOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => {
                    setIsArtistDropdownOpen(false)
                  }, 120)
                }}
                placeholder="Type artist name"
                autoComplete="off"
                className="block w-full rounded border border-gray-300 px-4 py-2 leading-tight focus:border-gray-500 focus:bg-white focus:outline-none"
              />

              {isArtistDropdownOpen && (
                <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                  {normalizedArtistSearchQuery.length < 2 && (
                    <p className="px-3 py-2 text-sm text-gray-500">
                      Type at least 2 letters to search.
                    </p>
                  )}

                  {isLoadingArtistSearch &&
                    normalizedArtistSearchQuery.length >= 2 && (
                      <p className="px-3 py-2 text-sm text-gray-500">
                        Loading...
                      </p>
                    )}

                  {!isLoadingArtistSearch &&
                    normalizedArtistSearchQuery.length >= 2 &&
                    artistSuggestions.length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-500">
                        No artists found.
                      </p>
                    )}

                  {!isLoadingArtistSearch &&
                    artistSuggestions.map((artist) => (
                      <button
                        key={artist.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-purple-50"
                        onMouseDown={() => handleSelectedArtists(artist)}
                      >
                        {artist.title}
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={hasAlbum}
                  onChange={(e) => {
                    setHasAlbum(e.currentTarget.checked)
                    if (!e.currentTarget.checked) {
                      setValue('albumId', null)
                      setValue('trackNumber', null)
                      setSelectedAlbum(null)
                      setAlbumSearchQuery('')
                      setIsAlbumDropdownOpen(false)
                    }
                  }}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Tem álbum?
                </span>
              </label>
            </div>
          </div>

          <div className="mt-2 grid grid-cols-3 items-end gap-4 base:grid-cols-2 md:grid-cols-1">
            <div className="relative">
              <h4 className="font-bold text-sm my-3">Musical Genre</h4>
              <input type="hidden" {...register('genreId')} />
              <input
                value={genreSearchQuery}
                onChange={(event) => {
                  const nextValue = event.currentTarget.value
                  setGenreSearchQuery(nextValue)
                  setSelectedGenre(null)
                  setValue('genreId', '', { shouldValidate: true })
                  setIsGenreDropdownOpen(true)
                }}
                onFocus={() => setIsGenreDropdownOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => {
                    setIsGenreDropdownOpen(false)

                    if (!selectedGenre) {
                      setGenreSearchQuery('')
                    }
                  }, 120)
                }}
                placeholder="Type genre name"
                autoComplete="off"
                className="block w-full rounded border border-gray-300 px-4 py-2 leading-tight focus:border-gray-500 focus:bg-white focus:outline-none"
              />

              {isGenreDropdownOpen && (
                <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                  {normalizedGenreSearchQuery.length < 1 && (
                    <p className="px-3 py-2 text-sm text-gray-500">
                      Type at least 1 letter to search.
                    </p>
                  )}

                  {isLoadingGenreOptions &&
                    normalizedGenreSearchQuery.length >= 1 && (
                      <p className="px-3 py-2 text-sm text-gray-500">
                        Loading...
                      </p>
                    )}

                  {!isLoadingGenreOptions &&
                    normalizedGenreSearchQuery.length >= 1 &&
                    genreSuggestions.length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-500">
                        No genres found.
                      </p>
                    )}

                  {!isLoadingGenreOptions &&
                    genreSuggestions.map((genre) => (
                      <button
                        key={genre.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-purple-50"
                        onMouseDown={() => handleSelectGenre(genre)}
                      >
                        {genre.title}
                      </button>
                    ))}
                </div>
              )}
            </div>
            <Select
              options={licensesOptions}
              label="License"
              name="licenseId"
              register={register}
              required
            />
            <Select
              options={moodsOptions}
              label="Moods"
              name="moodIds"
              register={register}
              onChange={(e) => handleSelectedMood(e.currentTarget.value)}
            />
          </div>

          {hasAlbum && (
            <div className="mt-2 grid grid-cols-3 items-end gap-4 base:grid-cols-2 md:grid-cols-1">
              <div className="relative">
                <h4 className="font-bold text-sm my-3">Album</h4>
                <input type="hidden" {...register('albumId')} />
                <input
                  value={albumSearchQuery}
                  onChange={(event) => {
                    const nextValue = event.currentTarget.value
                    setAlbumSearchQuery(nextValue)
                    setSelectedAlbum(null)
                    setValue('albumId', '', { shouldValidate: true })
                    setIsAlbumDropdownOpen(true)
                  }}
                  onFocus={() => setIsAlbumDropdownOpen(true)}
                  onBlur={() => {
                    window.setTimeout(() => {
                      setIsAlbumDropdownOpen(false)

                      if (!selectedAlbum) {
                        setAlbumSearchQuery('')
                      }
                    }, 120)
                  }}
                  placeholder="Type album name"
                  autoComplete="off"
                  className="block w-full rounded border border-gray-300 px-4 py-2 leading-tight focus:border-gray-500 focus:bg-white focus:outline-none"
                />

                {isAlbumDropdownOpen && (
                  <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    {normalizedAlbumSearchQuery.length < 1 && (
                      <p className="px-3 py-2 text-sm text-gray-500">
                        Type at least 1 letter to search.
                      </p>
                    )}

                    {normalizedAlbumSearchQuery.length >= 1 &&
                      albumSuggestions.length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-500">
                          No albums found.
                        </p>
                      )}

                    {albumSuggestions.map((album) => (
                      <button
                        key={album.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-purple-50"
                        onMouseDown={() => handleSelectAlbum(album)}
                      >
                        {album.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Input
                placeholder="1, 2, 3..."
                label="Track Number"
                name="trackNumber"
                type="number"
                min="1"
                max="999"
                register={register}
                required
              />
            </div>
          )}
          <div className="mt-2 grid grid-cols-[250px,1fr] items-end gap-4 base:grid-cols-1">
            <div>
              <p className="font-bold text-sm my-3">
                {paletColorsArtwork.length > 0
                  ? 'Select a color'
                  : music?.color && 'Color'}
              </p>
              <div className="flex gap-2 items-center">
                {paletColorsArtwork.length > 0 ? (
                  paletColorsArtwork.map((color) => (
                    <button
                      type="button"
                      style={{ background: color }}
                      key={color}
                      className={`h-8 w-8 rounded-full ${
                        selectColor === color && 'border-2 border-black'
                      }`}
                      title={color}
                      onClick={() => {
                        setValue('color', color)
                        setSelectColor(color)
                      }}
                    />
                  ))
                ) : (
                  <>
                    {music?.color && (
                      <div
                        style={{ background: music.color }}
                        className="w-8 h-8 rounded-full "
                        title={music?.color}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {music?.id && (
            <div className="mt-6">
              <p className="font-bold">Catalog Governance</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <CatalogStatusBadge label="Discover" value={discoveryStatus} />
                <CatalogStatusBadge label="Visual" value={visualAssetStatus} />
                <CatalogStatusBadge label="Playback" value={playbackStatus} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4 base:grid-cols-1">
                <div className="rounded-xl border border-gray-200 p-4">
                  <Select
                    options={visualAssetStatusOptions}
                    label="Visual Asset Status"
                    value={visualAssetStatus}
                    onChange={(e) =>
                      setVisualAssetStatus(
                        e.currentTarget.value as VisualAssetStatus,
                      )
                    }
                  />
                  <Button
                    title="Update Visual Status"
                    className="mt-4"
                    variant="outline"
                    type="button"
                    isLoading={governanceActionLoading === 'visualAsset'}
                    onClick={handleUpdateMusicVisualAssetStatus}
                  />
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <Select
                    options={playbackStatusOptions}
                    label="Playback Status"
                    value={playbackStatus}
                    onChange={(e) =>
                      setPlaybackStatus(e.currentTarget.value as PlaybackStatus)
                    }
                  />
                  <Button
                    title="Update Playback Status"
                    className="mt-4"
                    variant="outline"
                    type="button"
                    isLoading={governanceActionLoading === 'playback'}
                    onClick={handleUpdateMusicPlaybackStatus}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="h-[1px] bg-gray-300/50 my-7" />

        <div className="ml-auto flex w-full justify-end gap-2 md:flex-col">
          {music?.id && (
            <Button
              title={discoveryStatus === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              className="max-w-[140px] md:max-w-full"
              variant="outline"
              isLoading={isPublisherLoading}
              onClick={() => handlePublisherMusic(music.id)}
              type="button"
            />
          )}

          <Button
            title={music ? 'Edit' : 'Save'}
            className="max-w-[140px] md:max-w-full"
            variant="purple"
            isLoading={isLoading}
          />
        </div>
      </form>
    </>
  )
}
